/* eslint-disable max-len */
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Divider from 'components/divider/Divider';
import Button from '@mui/material/Button';

import { initialQueryParamsState, usePaginatedGetReceiptsQuery } from './hooks/usePaginatedGetReceiptsQuery';
import { CustomPopover, If, InfiniteLoadingSpinner, SearchWithDropdown } from 'components';
import { CaretDownIcon, ExportIcon, InvoicesPageIcon, NoSearchResultsIcon, SearchIcon } from 'assets/icons';
import {
  getNotifications,
  selectCurrentTeam,
  selectCurrentTeamInvoicePermissions,
  selectMerchantTeams,
  setCurrentTeam
} from 'containers/app/appSlice';
import { areEqualArrays, constructArrayAsValue, debounce } from 'utils/commonUtils';
import isEqual from 'lodash/isEqual';
import useIntersectionObserver from 'hooks/useIntersectionObserver';
import { Receipt } from './receipt.model';
import { useAppSelector } from 'hooks';
import ReceiptFilters from './components/ReceiptFilters';
import { ReceiptStatus } from './receipts.model';
import { useLazyDownloadReceiptsQuery } from './api';
import ReceiptsTable from './components/ReceiptsTable';
import { ExportMenu } from 'containers/invoices/components';
import { FileTypes, clevertapEvents } from 'types/baseTypes';
import { useAppDispatch } from 'hooks';
import { showNotifier } from 'containers/app/appSlice';
import { NotifierTypes } from 'constants/notifierConstants';
import { ReceiptRoutesState } from 'routes/types';
import DateUtils from 'utils/dateUtils';
import { PopoverArrowTypes, searchTypes } from 'constants/common';
import { logAnalyticEvent } from 'utils/analytics';
import routesPath from 'routes/RoutesPath';
import { ReceiptsExportPopover } from './constants';
import { InfoMesssages } from 'types/infoMessages';
import { ROOT_ROUTE_STATE } from 'routes/constants';
import { resetReceiptState } from './receiptsSlice';
import { resetInvoiceState } from 'containers/invoices/InvoicesSlice';
import { PaymentSystem } from 'containers/payouts/constants';

const Receipts: FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const currentTeam = useAppSelector(selectCurrentTeam);
  const receiptPermissions = useAppSelector(selectCurrentTeamInvoicePermissions);
  const [selectedReceipts, setSelectedReceipts] = useState<Array<string>>([]);
  const [currentReceiptId, setCurrentReceiptId] = useState('');
  const [searchText, setSearchText] = useState('');
  const [selectedCustomers, setSelectedCustomers] = useState<string[]>([]);
  const [selectedMerchants, setSelectedMerchants] = useState<string[]>([]);
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [paymentMethods, setPaymentMethods] = useState<string[]>([]);
  const [types, setTypes] = useState<string[]>([]);
  const [statuses, setStatuses] = useState<ReceiptStatus[]>([]);
  const [tipIncluded, setTipIncluded] = useState<string[]>([]);
  const [isExportPopoverOpen, setIsExportPopoverOpen] = useState({ open: false, id: '' });
  const [showFiltersAndExport, setShowFiltersAndExport] = useState<boolean>(false);
  const [downloadReceiptsQuery] = useLazyDownloadReceiptsQuery();
  const merchantTeams = useAppSelector(selectMerchantTeams);
  const dispatch = useAppDispatch();
  const receiptDetailsId = searchParams.get('id');
  const teamId = searchParams.get('teamId');
  const [clicked, setClicked] = useState(false);
  const notifications = useAppSelector(getNotifications);
  const { teamId: teamIdFromUrl } = useParams();

  const {
    list: receiptsList,
    isLoading: isLoadingReceipts,
    loadMore,
    resetQuery: resetReceiptsQuery,
    hasMore: hasMoreReceipts,
    setQueryParams: setReceiptsQueryParams,
    queryParams: receiptsQueryParams,
    totalListCount: receiptsCount,
    isLoadingSuccess: receiptLoadingSuccess,
    hasPermissionsToView,
    searchType,
    setSearchType
  } = usePaginatedGetReceiptsQuery();

  const isInitialQueryParamsState = () => {
    return isEqual(receiptsQueryParams, initialQueryParamsState);
  };

  useEffect(() => {
    resetReceiptsQuery();
    setShowFiltersAndExport(false);
    setSelectedCustomers([]);
    setSelectedMerchants([]);
    setPaymentMethods([]);
    setStartDate(null);
    setEndDate(null);
    setTipIncluded([]);
    setStatuses([]);
    setTypes([]);
  }, [currentTeam?.id]);

  useEffect(() => {
    resetReceiptsQuery();
  }, [receiptPermissions.viewReceiptsBySelf, receiptPermissions.viewReceiptsByTeam]);

  useEffect(() => {
    const routeState = location.state as ReceiptRoutesState;

    if (routeState?.isToday) {
      const today = new Date();
      setStartDate(DateUtils.getDateInFormat({ date: today, dateFormat: 'yyyy-MM-dd' }));
      setEndDate(DateUtils.getDateInFormat({ date: today, dateFormat: 'yyyy-MM-dd' }));
      setStatuses([ReceiptStatus.SUCCESS]);
      setShowFiltersAndExport(true);
    }
    const teamIdVal = teamIdFromUrl ? teamIdFromUrl : teamId;
    if (teamIdVal && currentTeam?.id !== +teamIdVal) {
      const merchantTeam = merchantTeams.find(team => team.id === +teamIdVal);
      if (merchantTeam) {
        dispatch(setCurrentTeam(merchantTeam));
        dispatch(resetReceiptState());
        dispatch(resetInvoiceState());
      } else {
        navigate(routesPath.RECEIPTS, { replace: true });
      }
    }
  }, [location]);

  useEffect(() => {
    if (receiptDetailsId && receiptLoadingSuccess) {
      navigate(routesPath.RECEIPTS, { replace: true });
      handleReceiptClick(receiptDetailsId);
    }
  }, [receiptDetailsId, receiptLoadingSuccess]);

  useEffect(() => {
    let updatedParams = {
      ...receiptsQueryParams,
      customerId: selectedCustomers,
      merchantId: selectedMerchants,
      paymentMethod: paymentMethods,
      tipAmountNE: tipIncluded.length ? 0 : null,
      sourceType: types,
      status: statuses
    };
    updatedParams = {
      ...updatedParams,
      start: DateUtils.ensureUTCStartDateFormat(startDate) || null,
      end: DateUtils.ensureUTCEndDateFormat(endDate) || null
    };
    setReceiptsQueryParams(prevQueryParams => ({
      ...prevQueryParams,
      ...updatedParams
    }));
  }, [
    selectedCustomers,
    selectedMerchants,
    paymentMethods,
    tipIncluded,
    startDate,
    endDate,
    statuses,
    types,
    searchType
  ]);

  useEffect(() => {
    if (!isLoadingReceipts && receiptsList.length > 0) {
      setShowFiltersAndExport(true);
    }
  }, [isLoadingReceipts]);

  const receiptListLoader = useRef(loadMore);

  const { setIntersectionElement } = useIntersectionObserver({
    intersectionCallBack: receiptListLoader,
    threshold: 0.5
  });

  useEffect(() => {
    receiptListLoader.current = loadMore;
  }, [loadMore]);

  const isSelectedReceipt = (receipt: Receipt) => {
    return selectedReceipts.find((id: string) => receipt.id === id);
  };

  const onToggleReceiptSelection = (receipt: Receipt, isCheckdInvoice: boolean) => {
    if (isCheckdInvoice) {
      setSelectedReceipts(checkedReceiptsState => checkedReceiptsState.filter(id => id !== receipt.id));
      return;
    }
    setSelectedReceipts([...selectedReceipts, receipt.id]);
  };

  const extractIdsFromLoadedReceipts = () => {
    return receiptsList.map(receipt => receipt.id);
  };

  const areAllLoadedReceiptsSelected = () => {
    return areEqualArrays({ primaryArray: extractIdsFromLoadedReceipts(), secondaryArray: selectedReceipts });
  };

  const onToggleSelectAllLoadedReceipts = () => {
    if (areAllLoadedReceiptsSelected()) {
      setSelectedReceipts([]);
    } else {
      setSelectedReceipts(extractIdsFromLoadedReceipts());
    }
  };

  const initiateSearch = (searchTerm?: string) => {
    setReceiptsQueryParams(prevQueryParams => ({ ...prevQueryParams, search: searchTerm }));
  };
  const debouncedSearchHandler = useCallback(debounce(initiateSearch, 500), [receiptsQueryParams]);

  const onSearchTermChange = (searchTerm: string) => {
    setSearchText(searchTerm);
    debouncedSearchHandler(searchTerm);
  };

  const onClearSearch = () => {
    setSearchText('');
    initiateSearch(null);
  };

  const exportAnchor = () => {
    return (
      <Button
        className="min-h-[38px] bg-[#A4D7FA]
        font-lato font-normal normal-case text-primaryText shadow-none hover:bg-[#80C3F1]"
        variant="contained"
        endIcon={<CaretDownIcon className="path-stroke-current w-3 shrink-0 text-accentText" />}
        startIcon={<ExportIcon className="shrink-0" />}
        disableRipple={isExportDisabled}>
        <div className="text-sbase font-semibold">Export</div>
      </Button>
    );
  };

  const handleSidePanelClose = () => {
    setCurrentReceiptId('');
    navigate(routesPath.RECEIPTS);
  };

  const handleReceiptClick = (receiptId: string) => {
    navigate(`/payments/receipts/${currentTeam?.id}/${receiptId}/receipt`, { state: ROOT_ROUTE_STATE });
  };

  const handleRefundTransactionClick = (receiptId: string) => {
    navigate(`/payments/receipts/${currentTeam?.id}/${receiptId}/refund`, { state: ROOT_ROUTE_STATE });
  };

  const handleDisputeTransactionClick = (disputeId: string, receiptId: string) => {
    navigate(`/payments/receipts/${currentTeam?.id}/${receiptId}/dispute/${disputeId}`, {
      state: ROOT_ROUTE_STATE
    });
  };

  const handleShareReceiptClick = (invoiceId: string, receiptId: string) => {
    navigate(`/payments/receipts/${currentTeam?.id}/${receiptId}/share-receipt/${receiptId}`, {
      state: ROOT_ROUTE_STATE
    });
  };

  const handleCreateRefundClick = (receiptId: string) => {
    navigate(`/payments/receipts/${currentTeam?.id}/${receiptId}/create-refund`, { state: ROOT_ROUTE_STATE });
  };

  const handlePayoutClick = (payoutId: string, receiptId: string, paymentSystem: PaymentSystem) => {
    const type = paymentSystem || PaymentSystem.LIP;
    navigate(`/payments/receipts/${currentTeam?.id}/${receiptId}/payout/${payoutId}?type=${type}`, {
      state: ROOT_ROUTE_STATE
    });
    setCurrentReceiptId(receiptId);
  };
  const handleExportClick = (id: string) => {
    if (isExportDisabled) {
      onInfoClick('receipts-export', InfoMesssages.noReceiptsToExport);
      return;
    }
    setIsExportPopoverOpen(prevState => ({ open: !prevState.open, id }));
  };

  const onCloseExportPopover = (id: string) => {
    if (isExportPopoverOpen.id === id) {
      setIsExportPopoverOpen({ open: false, id: '' });
    }
  };

  const handleSearchIconClick = () => {
    setClicked(true);
  };

  const onDownloadFile = (fileType: FileTypes, id: string) => {
    downloadReceiptsQuery({
      teamId: currentTeam?.id,
      ...receiptsQueryParams,
      limit: null,
      page: 0,
      type: fileType,
      selectedIds: selectedReceipts
    });
    dispatch(
      showNotifier({
        message: {
          primaryMessage: 'Downloading Receipt may take few seconds',
          secondaryMessage: ''
        },
        type: NotifierTypes.INFO,
        showClose: false,
        fontStyle: 'text-primary font-normal'
      })
    );
    logAnalyticEvent(clevertapEvents.webReceiptsListExport, {
      ...constructArrayAsValue('Customers', receiptsQueryParams.customerId),
      ...constructArrayAsValue('Created by', receiptsQueryParams.merchantId),
      'Start Date': receiptsQueryParams.start,
      'End Date': receiptsQueryParams.end,
      ...constructArrayAsValue('Status', receiptsQueryParams.status),
      ...constructArrayAsValue('Payment Method', receiptsQueryParams.paymentMethod),
      ...constructArrayAsValue('Type', receiptsQueryParams.sourceType),
      'Tip Collected': receiptsQueryParams.tipAmountNE
    });
    onCloseExportPopover(id);
  };

  const isFilterResultEmpty =
    receiptsList?.length === 0 &&
    !isLoadingReceipts &&
    !isInitialQueryParamsState() &&
    receiptsQueryParams.search === null;

  const isSearchResultEmpty =
    receiptsList?.length === 0 &&
    !isLoadingReceipts &&
    receiptsQueryParams.search !== null &&
    receiptsQueryParams.search !== '';

  const isReceiptResultEmpty =
    receiptsList?.length === 0 &&
    !isLoadingReceipts &&
    isInitialQueryParamsState() &&
    receiptsQueryParams.search === null &&
    receiptLoadingSuccess;

  const isExportDisabled = isFilterResultEmpty || isSearchResultEmpty;

  useEffect(() => {
    setSelectedReceipts([]);
  }, [receiptsQueryParams]);

  const onInfoClick = (id: string, infoMessage) => {
    const existingNotification = notifications.find(notification => notification.id === id);
    if (existingNotification) return;
    dispatch(
      showNotifier({
        id,
        message: {
          primaryMessage: infoMessage,
          secondaryMessage: '',
          isMessageHtml: true
        },
        type: NotifierTypes.WARNING,
        showClose: true,
        fontStyle: 'text-primary font-normal'
      })
    );
  };

  return (
    <>
      <div className="overflow-y flex h-screen flex-col justify-between px-6 pb-20 pt-6 sm:p-6">
        <div className="flex justify-between">
          <div className="mb-8 flex flex-row items-center gap-3">
            <div className="text-2xl font-bold text-primaryText">Receipts</div>
            <If condition={!clicked}>
              <div onClick={handleSearchIconClick}>
                <SearchIcon className="mx-2 flex lg:hidden" />
              </div>
            </If>
            <SearchWithDropdown
              selectedOption={searchType}
              setSelectedOption={setSearchType}
              menuItems={searchTypes}
              searchId="receipt-search"
              onClearSearchInput={onClearSearch}
              onSearchInputChange={onSearchTermChange}
              searchInputvalue={searchText}
              wrapperStyle={`w-38 h-10 cursor-pointer ${!hasPermissionsToView ? 'pointer-events-none' : ''} 
            ${!clicked ? 'lg:flex sm:hidden' : ''} `}
              autoFocus={!!hasPermissionsToView}
            />
          </div>
          <div className="ml-1 flex self-start lg:hidden">
            <CustomPopover
              show={isExportPopoverOpen.open && isExportPopoverOpen.id === ReceiptsExportPopover.TAB_VIEW}
              anchorComponent={exportAnchor}
              anchorComponentId="receipt-export-tablet-button"
              onTogglePopover={() => handleExportClick(ReceiptsExportPopover.TAB_VIEW)}
              onClosePopover={() => onCloseExportPopover(ReceiptsExportPopover.TAB_VIEW)}
              offset="right-0 top-4"
              highlightAnchor={false}
              minWidth="min-w-[160px]"
              showArrow={true}
              arrowType={PopoverArrowTypes.TOP}
              margin="mt-8"
              popoverBodyStyle="p-2">
              <ExportMenu
                id="receipts-export"
                onDownloadFile={fileType => onDownloadFile(fileType, ReceiptsExportPopover.TAB_VIEW)}
              />
            </CustomPopover>
          </div>
        </div>
        {hasPermissionsToView && (
          <>
            <If condition={showFiltersAndExport}>
              <div className="relative flex justify-between">
                <ReceiptFilters
                  currentTeamId={currentTeam?.id}
                  selectedCustomers={selectedCustomers}
                  setSelectedCustomers={setSelectedCustomers}
                  selectedMerchants={selectedMerchants}
                  setSelectedMerchants={setSelectedMerchants}
                  paymentMethods={paymentMethods}
                  setPaymentMethods={setPaymentMethods}
                  startDate={startDate}
                  endDate={endDate}
                  setStartDate={setStartDate}
                  setEndDate={setEndDate}
                  tipIncluded={tipIncluded}
                  setTipIncluded={setTipIncluded}
                  types={types}
                  statuses={statuses}
                  setStatuses={setStatuses}
                  setTypes={setTypes}
                />
                <div className="hidden self-start lg:flex">
                  <CustomPopover
                    show={isExportPopoverOpen.open && isExportPopoverOpen.id === ReceiptsExportPopover.WEB_VIEW}
                    anchorComponent={exportAnchor}
                    anchorComponentId="receipt-export-button"
                    onTogglePopover={() => handleExportClick(ReceiptsExportPopover.WEB_VIEW)}
                    onClosePopover={() => onCloseExportPopover(ReceiptsExportPopover.WEB_VIEW)}
                    offset="right-0 top-4"
                    highlightAnchor={false}
                    minWidth="min-w-[160px]"
                    showArrow={true}
                    arrowType={PopoverArrowTypes.TOP}
                    margin="mt-8"
                    popoverBodyStyle="p-2">
                    <ExportMenu
                      id="receipts-export"
                      onDownloadFile={fileType => onDownloadFile(fileType, ReceiptsExportPopover.WEB_VIEW)}
                    />
                  </CustomPopover>
                </div>
              </div>
            </If>
            <div className="h-10">
              <If condition={receiptsCount !== 0}>
                <If condition={!isLoadingReceipts}>
                  <div id="total-receipts-count" className="mb-4 mt-1 text-sbase text-accentText">
                    {` Total ${receiptsCount} ${receiptsCount === 1 ? 'receipt' : 'receipts'} `}
                  </div>
                  <Divider className="mb-1 w-full border-secondaryBtn" />
                </If>
              </If>
            </div>
          </>
        )}

        <div className={`customNormalScroll mt-1 grow ${isLoadingReceipts && hasMoreReceipts ? 'pb-0' : 'pb-20'}`}>
          <If condition={isReceiptResultEmpty && hasPermissionsToView}>
            <div className="flex h-full flex-col items-center justify-center gap-5">
              <div className="flex flex-col items-center gap-5">
                <InvoicesPageIcon className="h-10 w-10" />
                <div className="text-sbase text-primaryText">No receipts found</div>
              </div>
            </div>
          </If>
          <If condition={isFilterResultEmpty && hasPermissionsToView}>
            <div className="flex h-full flex-col items-center justify-center gap-5">
              <div className="flex flex-col items-center gap-5">
                <InvoicesPageIcon className="h-10 w-10" />
                <div className="text-sbase text-primaryText">No receipts found for selected filters</div>
              </div>
            </div>
          </If>

          <If condition={isSearchResultEmpty && hasPermissionsToView}>
            <div className="flex h-full flex-col items-center justify-center gap-5">
              <div className="flex flex-col items-center gap-5">
                <NoSearchResultsIcon />
                <div className="text-sbase text-primaryText">No results found!</div>
              </div>
            </div>
          </If>

          <If condition={!isLoadingReceipts && !hasPermissionsToView}>
            <div className="flex h-full flex-col items-center justify-center gap-5">
              <div className="flex flex-col items-center gap-5">
                <InvoicesPageIcon className="h-10 w-10" />
                <div className="text-sbase text-primaryText">You do not have permissions to view Receipts</div>
              </div>
            </div>
          </If>
          <If condition={receiptsList?.length !== 0 && hasPermissionsToView}>
            <ReceiptsTable
              areAllLoadedReceiptsSelected={areAllLoadedReceiptsSelected}
              receiptsList={receiptsList}
              isSelectedReceipt={isSelectedReceipt}
              onToggleReceiptSelection={onToggleReceiptSelection}
              onToggleSelectAllLoadedReceipts={onToggleSelectAllLoadedReceipts}
              onReceiptClick={handleReceiptClick}
              onPayoutClick={handlePayoutClick}
              currentSelectedId={currentReceiptId}
              onRefundTransactionClick={handleRefundTransactionClick}
              onShareReceiptClick={handleShareReceiptClick}
              onCreateRefundClick={handleCreateRefundClick}
              onDisputedTransactionClick={handleDisputeTransactionClick}
            />
          </If>

          <If condition={!isLoadingReceipts && hasMoreReceipts}>
            <div className="h-2" ref={setIntersectionElement}></div>
          </If>
          <If condition={isLoadingReceipts}>
            <InfiniteLoadingSpinner />
          </If>
        </div>
      </div>
      <Outlet context={{ handleSidePanelClose, resetReceiptsQuery }} />
    </>
  );
};

export default Receipts;
